import {
  CalendarClock,
  Clock,
  Lightbulb,
  Palmtree,
  RefreshCw,
  Video,
} from 'lucide-react'
import {
  FigmaMark,
  GithubMark,
  GmailMark,
  GoogleCalendarMark,
  LinearMark,
  NotionMark,
  OnePasswordMark,
  PostHogMark,
  PostmanMark,
  SentryMark,
  SlackMark,
  VercelMark,
  getLoop3App,
} from '@loop3/ui'
import type { ComponentType, SVGProps } from 'react'
import { MarkerMark } from '@/components/icons/MarkerMark'
import { L3OS_SYNC_PATH, SOLUTIONS_LIBRARY_PATH } from '@/lib/routes'

export type IconType = ComponentType<SVGProps<SVGSVGElement>>

interface LinkItemBase {
  id: string
  title: string
  description: string
  icon: IconType
}

/** A hub link: an in-app route (`to`) or an external URL (`href`), never both. */
export type LinkItem =
  | (LinkItemBase & { to: string; href?: never })
  | (LinkItemBase & { href: string; to?: never })

/** URL of a Loop3 app from the shared registry; throws on an unknown id. */
export function loop3AppUrl(id: string): string {
  const app = getLoop3App(id)
  if (!app) throw new Error(`Unknown Loop3 app: ${id}`)
  return app.url
}

/**
 * L3OS-built tools. Some live inside the app (`to`), others are peer
 * apps on separate domains (`href`).
 */
export const INTERNAL_TOOLS: readonly LinkItem[] = [
  {
    id: 'time-off',
    title: 'Time Off',
    description: 'Request PTO and check your vacation balance.',
    icon: Palmtree,
    href: loop3AppUrl('timeoff'),
  },
  {
    id: 'timesheet',
    title: 'Timesheet',
    description: 'Track hours per client per week.',
    icon: Clock,
    href: loop3AppUrl('timesheet'),
  },
  {
    id: 'calendar',
    title: 'Calendar',
    description: 'Booking links for you and your client squads.',
    icon: CalendarClock,
    href: loop3AppUrl('calendar'),
  },
  {
    id: 'meetings',
    title: 'Meetings Hub',
    description: 'Meeting recordings, summaries, and follow-ups.',
    icon: Video,
    href: loop3AppUrl('meetings'),
  },
  {
    id: 'solutions',
    title: 'Solutions library',
    description: 'Everything Loop3 has built, by client, capability and stage.',
    icon: Lightbulb,
    to: SOLUTIONS_LIBRARY_PATH,
  },
  {
    id: 'l3os-sync',
    title: 'L3OS Sync',
    description:
      'Skills, workflows, and configs shared across every managed repo.',
    icon: RefreshCw,
    to: L3OS_SYNC_PATH,
  },
]

/**
 * Daily-use third-party tools the whole team touches. Curated shortlist
 * ordered roughly by daily-use frequency. The full catalog (with
 * categories, client mappings, tutorials) lives in the Notion Tech Stack
 * database — these are just the top-of-mind shortcuts.
 */
export const DAILY_TOOLS: readonly LinkItem[] = [
  {
    id: 'linear',
    title: 'Linear',
    description: 'Issue tracking and project management',
    icon: LinearMark,
    href: 'https://linear.app/loop3',
  },
  {
    id: 'notion',
    title: 'Notion',
    description: 'Team workspace, meetings, and docs',
    icon: NotionMark,
    href: 'https://www.notion.so/1ba5b40905f249e1a607c0966cfce5f7',
  },
  {
    id: '1password',
    title: '1Password',
    description: 'Shared credentials vault',
    icon: OnePasswordMark,
    href: 'https://loop3.1password.com/',
  },
  {
    id: 'slack',
    title: 'Slack',
    description: 'Team communication',
    icon: SlackMark,
    href: 'https://loop3studio.slack.com/',
  },
  {
    id: 'figma',
    title: 'Figma',
    description: 'Design files and prototypes',
    icon: FigmaMark,
    href: 'https://www.figma.com/files/team/1335658926750119178/project/202065273',
  },
  {
    id: 'github',
    title: 'GitHub',
    description: 'Source repositories and code review',
    icon: GithubMark,
    href: 'https://github.com/Loop3Studio',
  },
  {
    id: 'vercel',
    title: 'Vercel',
    description: 'Deployments and hosting',
    icon: VercelMark,
    href: 'https://vercel.com/loop3-team',
  },
  {
    id: 'gmail',
    title: 'Gmail',
    description: 'Loop3 email',
    icon: GmailMark,
    href: 'https://gmail.com/',
  },
  {
    id: 'calendar',
    title: 'Google Calendar',
    description: 'Team calendar and meetings',
    icon: GoogleCalendarMark,
    href: 'https://calendar.google.com/',
  },
  {
    id: 'postman',
    title: 'Postman',
    description: 'API client and testing',
    icon: PostmanMark,
    href: 'https://loop3-team.postman.co/',
  },
  {
    id: 'posthog',
    title: 'PostHog',
    description: 'Product analytics and session replay',
    icon: PostHogMark,
    href: 'https://us.posthog.com/',
  },
  {
    id: 'marker',
    title: 'Marker.io',
    description: 'Visual bug reporting for client websites',
    icon: MarkerMark,
    href: 'https://app.marker.io/',
  },
  {
    id: 'sentry',
    title: 'Sentry',
    description: 'Error tracking and performance monitoring',
    icon: SentryMark,
    href: 'https://loop3studio.sentry.io/',
  },
]
